import mixpanel from "mixpanel-browser";

/**
 * Tek bir olayı hem Mixpanel'e hem de GTM dataLayer'ına yazan sarmalayıcı.
 *
 * Ürün 52 olay üretiyordu ama hiçbiri dataLayer'a ulaşmıyordu; GTM'in elinde
 * tetikleyecek tek şey buton metni kaldığı için tüm ölçüm Türkçe metne
 * bağlanmıştı. İki hedefi tek çağrıda yazmak, ikisinin bir daha ayrışmamasını
 * sağlıyor.
 *
 * Buradaki hiçbir şey kullanıcı akışına hata fırlatmaz — ölçüm bir işlemi
 * asla bozmamalı.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type AnalyticsProps = Record<string, unknown>;

/**
 * Yalnızca dataLayer'a yazar. GA4 e-ticaret olayları gibi Mixpanel tarafında
 * karşılığı olmaması gereken olaylar için.
 */
export const pushDataLayer = (payload: AnalyticsProps): void => {
  try {
    // GTM snippet'i index.html'de yükleniyor ama script'in çalışmadığı
    // durumlarda (reklam engelleyici, test ortamı) dizi hiç var olmayabilir.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (e) {
    console.warn("dataLayer push error", e);
  }
};

export interface TrackOptions {
  /**
   * dataLayer'da kullanılacak olay adı. GA4'ün beklediği adlar Mixpanel
   * taksonomisiyle birebir örtüşmüyor (`user_registered` → `sign_up`);
   * verilmezse Mixpanel adı kullanılır.
   */
  dataLayerEvent?: string;
  /**
   * dataLayer'a yazılacak alanlar.
   *
   * DİKKAT: Mixpanel olaylarının bir kısmı e-posta ve ad taşıyor; GA4'e kişisel
   * veri gönderilmesi yasak. props ile dataLayer alanları farklıysa bu alan
   * mutlaka verilmeli — verilmezse `props` olduğu gibi geçer.
   */
  dataLayerProps?: AnalyticsProps;
}

/**
 * Olayı iki hedefe birden yazar. Mixpanel başarısız olsa bile dataLayer
 * push'ı denenir; biri diğerini engellemez.
 */
export const track = (
  event: string,
  props: AnalyticsProps = {},
  options: TrackOptions = {},
): void => {
  try {
    if (typeof mixpanel?.track === "function") {
      mixpanel.track(event, props);
    }
  } catch (e) {
    console.warn("mx error", e);
  }

  pushDataLayer({
    event: options.dataLayerEvent ?? event,
    ...(options.dataLayerProps ?? props),
  });
};

export interface AdIdentity {
  gaClientId?: string;
  gaSessionId?: string;
  gclid?: string;
  wbraid?: string;
}

// Google'ın tıklama ilişkilendirme penceresi 90 gün; ilk temas değerini bundan
// daha uzun taşımanın anlamı yok.
const AD_IDENTITY_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const AD_IDENTITY_KEY = "harmony_ad_identity";

const readCookie = (name: string): string | undefined => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * `_ga` çerezi `GA1.1.<clientId>.<timestamp>` biçiminde; GA4'ün client id
 * dediği şey son iki parçanın birleşimi.
 */
const readGaClientId = (): string | undefined => {
  const raw = readCookie("_ga");
  if (!raw) return undefined;
  const parts = raw.split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : undefined;
};

/**
 * Oturum çerezi `_ga_<measurementId>` adıyla yazılıyor ve measurement id'yi
 * burada bilmiyoruz; bu yüzden ön ekle eşleşen ilk çerez okunuyor.
 * Değer `GS1.1.<sessionId>.<...>` biçiminde.
 */
const readGaSessionId = (): string | undefined => {
  try {
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("_ga_"));
    if (!cookie) return undefined;
    const value = cookie.slice(cookie.indexOf("=") + 1);
    const parts = value.split(".");
    return parts.length >= 3 ? parts[2] : undefined;
  } catch {
    return undefined;
  }
};

/**
 * URL'deki tıklama kimliğini ilk görüldüğü anda saklar. Kullanıcı ödemeye
 * ulaşana kadar OAuth ve Stripe üzerinden geçiyor; parametre URL'de
 * kalmadığı için ilk temas değeri korunmazsa ilişkilendirme kopuyor.
 */
const persistFirstTouch = (): { gclid?: string; wbraid?: string } => {
  let stored: { gclid?: string; wbraid?: string; at?: number } = {};

  try {
    const raw = localStorage.getItem(AD_IDENTITY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.at && Date.now() - parsed.at < AD_IDENTITY_TTL_MS) {
        stored = parsed;
      }
    }
  } catch {
    // Bozuk kayıt veya kapalı localStorage: baştan yazılır.
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid") ?? undefined;
    const wbraid = params.get("wbraid") ?? undefined;

    // Yeni bir tıklama her zaman öncekinin yerine geçer; aynı kullanıcının
    // ikinci reklam tıklaması son tıklamaya yazılmalı.
    if (gclid || wbraid) {
      stored = { gclid, wbraid, at: Date.now() };
      localStorage.setItem(AD_IDENTITY_KEY, JSON.stringify(stored));
    }
  } catch (e) {
    console.warn("ad identity persist error", e);
  }

  return { gclid: stored.gclid, wbraid: stored.wbraid };
};

/**
 * Checkout oturumuna iliştirilecek reklam kimliği.
 *
 * Stripe webhook'u tarayıcısız çalışıyor; bu değerler oturumla birlikte
 * taşınmazsa bir ödeme, onu üreten tıklamaya hiçbir zaman bağlanamıyor.
 * Alanların hepsi opsiyonel — reklam engelleyici, doğrudan ziyaret veya
 * süresi dolmuş çerez, değerin meşru olarak yok olduğu durumlar.
 */
export const getAdIdentity = (): AdIdentity => {
  const { gclid, wbraid } = persistFirstTouch();

  return {
    gaClientId: readGaClientId(),
    gaSessionId: readGaSessionId(),
    gclid,
    wbraid,
  };
};
